import { SKIPPER_AUTHOR_ID } from '../const'

// Kill switch. Flip to false + deploy to stop all GSC Pendo tracking (e.g. when the
// 500-message trial cap is near or the spike is over). A runtime override is also
// honored: set window.$Skipper.pendoAgentAnalytics = false to disable without a deploy.
const PENDO_TRACKING_ENABLED = true

// One Pendo agent per GSC domain. Support is intentionally absent — it is a human
// Zendesk agent, not an AI, so it is excluded from Agent Analytics. Unknown domains
// resolve to null and no-op.
const AGENT_IDS: Record<string, string> = {
  editor: '-L2aWnbC32_VNqMurph4r27f90M', // Pendo agent "GSC - Survey Editor"
  stats: 'tXeIXcbB-clNvQ054J_vo0U4YMs', // Pendo agent "GSC - Stats"
  query: 'HbHFd5cYmjJB63xAoiG2fg2_uGs', // Pendo agent "GSC - Query"
  generic: 'bPrD-6QAKIK4wvPAagJWLwN1GS0' // Pendo agent "GSC - Generic"
}

const THINKING_TEXTS = new Set(['...', 'Thinking...'])

// agent_response is delivered repeatedly by the polling loop until acked; dedup by
// messageId so each reply is reported exactly once. messageIds are UID-unique.
const trackedResponseIDs = new Set<string>()

type TrackAgentFn = (eventType: string, payload: Record<string, unknown>) => void

function trackAgentFn(): TrackAgentFn | null {
  if (!PENDO_TRACKING_ENABLED) {
    return null
  }
  const win = window as unknown as {
    pendo?: { trackAgent?: TrackAgentFn }
    $Skipper?: { pendoAgentAnalytics?: boolean }
  }
  if (false === win.$Skipper?.pendoAgentAnalytics) {
    return null
  }
  const trackAgent = win.pendo?.trackAgent
  return 'function' === typeof trackAgent ? trackAgent.bind(win.pendo) : null
}

function agentIdFor(domain: string | undefined): string | null {
  return domain ? (AGENT_IDS[domain] ?? null) : null
}

export function trackPrompt(params: {
  domain?: string
  conversationId: string | null
  messageId: string
  content: string
  suggestedPrompt: boolean
}): void {
  const track = trackAgentFn()
  const agentId = agentIdFor(params.domain)
  if (!track || !agentId || !params.conversationId) {
    return
  }
  track('prompt', {
    agentId,
    conversationId: params.conversationId,
    messageId: params.messageId,
    content: params.content,
    suggestedPrompt: params.suggestedPrompt
  })
}

export function trackAgentResponse(params: {
  domain?: string
  conversationId: string | null
  messageId: string
  content: string
}): void {
  const track = trackAgentFn()
  const agentId = agentIdFor(params.domain)
  if (!track || !agentId || !params.conversationId) {
    return
  }
  if (trackedResponseIDs.has(params.messageId)) {
    return
  }
  trackedResponseIDs.add(params.messageId)
  track('agent_response', {
    agentId,
    conversationId: params.conversationId,
    messageId: params.messageId,
    content: params.content
  })
}

// Scan a freshly-merged message list and emit agent_response for every finalized AI
// reply not yet reported. Shared by send.ts (post-send response) and the poll loop.
export function trackAgentResponses(params: {
  domain?: string
  conversationId: string | null
  messages: Array<{ id: string; author: string; text: string; error?: boolean }>
}): void {
  for (const message of params.messages) {
    if (message.author !== SKIPPER_AUTHOR_ID || message.error || THINKING_TEXTS.has(message.text)) {
      continue
    }
    trackAgentResponse({
      domain: params.domain,
      conversationId: params.conversationId,
      messageId: message.id,
      content: message.text
    })
  }
}

export function trackUserReaction(params: {
  domain?: string
  conversationId: string | null
  messageId: string
  like: boolean | null
}): void {
  const track = trackAgentFn()
  const agentId = agentIdFor(params.domain)
  if (!track || !agentId || !params.conversationId) {
    return
  }
  const content = true === params.like ? 'positive' : false === params.like ? 'negative' : 'unreact'
  track('user_reaction', {
    agentId,
    conversationId: params.conversationId,
    messageId: params.messageId,
    content
  })
}
